/**
 * Generic Drawer Web Component
 * Reusable slide-in panel with overlay, escape key handling, and focus trapping.
 * Used as base for cart drawer, filter drawer, specs drawer, etc.
 */
class DrawerComponent extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('.drawer__overlay')?.addEventListener('click', this.close.bind(this));

    // Listen for hash-based open/close requests from body-controller
    if (this.dataset.overlayHash) {
      document.addEventListener('overlay:request-open', this.onRequestOpen.bind(this));
      document.addEventListener('overlay:request-close-all', this.onRequestCloseAll.bind(this));
    }
  }

  onRequestOpen(event) {
    const { overlayId } = event.detail || {};
    if (overlayId === this.id && !this.classList.contains('active')) {
      this.open();
    }
  }

  onRequestCloseAll() {
    if (this.classList.contains('active')) {
      this.close();
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    // Timeout helps ensure animation triggers consistently
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.querySelector('.drawer__wrapper') || this;
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (typeof trapFocus === 'function') {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');

    // Dispatch overlay:open event for body-controller
    const overlayHash = this.dataset.overlayHash;
    if (overlayHash) {
      document.dispatchEvent(
        new CustomEvent('overlay:open', {
          detail: { hash: overlayHash, overlayId: this.id },
        })
      );
    }
  }

  close() {
    this.classList.remove('active');
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }
    document.body.classList.remove('overflow-hidden');

    // Dispatch overlay:close event for body-controller
    const overlayHash = this.dataset.overlayHash;
    if (overlayHash) {
      document.dispatchEvent(
        new CustomEvent('overlay:close', {
          detail: { overlayId: this.id },
        })
      );
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('drawer-component', DrawerComponent);
